/* eslint-disable react/no-did-update-set-state */
import * as React from 'react';

import ActionButton from 'components/buttons/action-button/ActionButton';
import ActionLink from 'components/buttons/action-link/ActionLink';
import ShippingStepSelectionAddress from '../ShippingStepSelectionAddress';
import { ModalFrameFooter, ModalFrameHeader, ModalFrameInner, ModalFrameMessage } from 'components/modal';
import { FormField } from 'components/form';
import Input from 'components/inputs/input/Input';

import { __t, applyTranslations } from 'utils/language';
import { GENERIC_REQUIRED_FIELD_MESSAGE, isEmail } from 'utils/validators';

import './shipping-step-selection.scss';

import type {
  ShippingAddress
} from 'types';

applyTranslations(require('./shipping-step-selection.i18n.json'));

type Props = {
  minHeight: number,
  selectedAddressId: number | undefined | null,
  list: Array<ShippingAddress>,
  email?: string | null,
  onEditAddress: (a: ShippingAddress) => void,
  onSelectAddressId: (a?: number | null) => void,
  onAddNewAddress: () => void,
  onBack: () => void,
  onSave: (a: ShippingAddress, email?: string | null) => void
};

type State = {
  selectedAddressId: number | undefined | null,
  email?: string | null,
  error?: string | null
};

class ShippingStepSelection extends React.PureComponent<Props, State> {
  state: State = {
    selectedAddressId: null,
    email: '',
    error: null
  };

  constructor(props: Props) {
    super(props);
    if (props.selectedAddressId) {
      this.state = {
        selectedAddressId: props.selectedAddressId,
        email: props.email || ''
      };
    } else if (props.list.length > 0) {
      this.state = {
        selectedAddressId: props.list[0].id,
        email: props.email || ''
      };
    }
  }

  componentDidMount() {
    const { onSelectAddressId } = this.props;
    const { selectedAddressId } = this.state;

    onSelectAddressId(selectedAddressId);
  }

  componentDidUpdate() {
    const { selectedAddressId } = this.props;

    if (selectedAddressId !== this.state.selectedAddressId) {
      this.setState(prevState => ({
        ...prevState,
        selectedAddressId
      }));
    }
  }

  handleAddNewAddress = (event: React.ChangeEvent<HTMLAnchorElement>): void => {
    event.preventDefault();

    const { onAddNewAddress } = this.props;

    onAddNewAddress();
  };

  handleAddressChanged = (addressId: number): void => {
    const { onSelectAddressId } = this.props;

    this.setState({
      selectedAddressId: addressId
    });

    onSelectAddressId(addressId);
  };

  handleEmailAddressChanged = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const value = event.target.value;

    this.setState(prevState => ({
      ...prevState,
      email: value
    }));
  };

  handleSave = (): void => {
    const { email } = this.state;
    const { list, selectedAddressId, onSave } = this.props;

    const validator = isEmail(email);

    if (validator.valid) {
      const listItem = list.find(ad => ad.id === selectedAddressId) ?? list[0];

      onSave(listItem, email);
    } else {
      this.setState(prevState => ({
        ...prevState,
        error: email ? validator.message : GENERIC_REQUIRED_FIELD_MESSAGE
      }));
    }
  };

  render(): React.ReactNode {
    const {
      list,
      onEditAddress,
      onBack,
      minHeight
    } = this.props;
    const { selectedAddressId, email, error } = this.state;

    return (
      <React.Fragment>
        <ModalFrameHeader
          title={__t('shipping_step_selection.your_shipping_details')}
        />
        <ModalFrameInner
          narrowContent
          minHeight={minHeight}
        >
          <ModalFrameMessage
            message={__t('shipping_step_selection.selection_title_address')}
          />
          <div className="shipping-step-selection__form-label">
            {__t('shipping_step_selection.select_address_label')}
          </div>
          <div className="shipping-step-selection__form">
            {
              list.map((address, index) =>
                <ShippingStepSelectionAddress
                  key={`address-${address.id || index}`}
                  address={address}
                  selectedAddressId={selectedAddressId}
                  onAddressChange={this.handleAddressChanged}
                  onEditAddress={onEditAddress}
                  index={index}
                />
              )
            }
          </div>
          <div>
            <ActionLink
              color="secondary"
              onClick={this.handleAddNewAddress}
            >
              {__t('shipping_step_selection.label_new_address')}
            </ActionLink>
          </div>
          <div className="shipping-step-selection__email-address">
            <div className="shipping-step-selection__email-address-section-label">
              {__t('shipping_step_selection.label_email_address')}
            </div>
            <FormField
              label={__t('shipping_step_selection.email_address')}
              direction="column"
              showRequiredMark
              labelClassName="shipping-step-selection__email-address-label"
              error={error}
            >
              <Input
                name="email"
                value={email || ''}
                onChange={this.handleEmailAddressChanged}
              />
            </FormField>
          </div>
        </ModalFrameInner>
        <ModalFrameFooter>
          <ActionButton
            color="tertiary"
            onClick={onBack}
          >
            {__t('shipping_step_selection.buttons.back')}
          </ActionButton>
          <ActionButton
            onClick={this.handleSave}
          >
            {__t('shipping_step_selection.buttons.continue')}
          </ActionButton>
        </ModalFrameFooter>
      </React.Fragment>
    );
  }
}

export default ShippingStepSelection;
